import { Icon } from '@platform/components';
import { MyProjectsModalProps } from '.';
import FlexibleLink from '../../../../util/FlexibleLink';
import { ProjectType } from '../../index';

export const RepIntroductionModalContent = (props: MyProjectsModalProps) => {
  const { userType, cardData, project } = { ...props };
  if (userType !== 'organization') {
    throw new Error('RepIntroductionModalContent is only for representatives');
  }
  const { professional, introduction, repTimezone } = cardData;
  const userInitiatedIntro = introduction.initiationType === 'Organization';
  const experteerName = professional.firstname + ' ' + professional.lastname;
  const introExpirationDate = new Date(
    new Date(introduction.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000,
  );

  const isCustom = !project;

  if (!introduction) {
    return null;
  }

  return (
    <div className='flex flex-col gap-6 text-md' data-testid='rep-introduction-modal'>
      <div className='flex flex-col md:grid md:grid-cols-2 gap-4'>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-name'>
          <div className='font-medium text-gray-500'>Name</div>
          <FlexibleLink
            newTab={true}
            className='text-sm font-semibold text-brand-600'
            href={`/platform/directory/professionals/${professional.id}`}
            type='internal'
          >
            {experteerName}
          </FlexibleLink>
        </div>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-timezone'>
          <div className='font-medium text-gray-500'>Timezone</div>
          <div className='text-sm font-semibold'>{repTimezone ?? ''}</div>
        </div>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-project'>
          <div className='font-medium text-gray-500'>Project</div>
          {project && (
            <FlexibleLink
              newTab={true}
              type='internal'
              className='text-sm font-semibold text-brand-600'
              href={`/platform/directory/projects/${project.id}`}
            >
              {project.name}
            </FlexibleLink>
          )}
          {!project && <div className='text-sm font-semibold'>Custom Project</div>}
        </div>
        <div className='flex flex-col gap-2' data-testid='introduction-modal-role'>
          <div className='font-medium text-gray-500'>Role</div>
          <div className='text-sm font-semibold'>
            {project?.roleType ? ProjectType[project.roleType] : 'Custom'}
          </div>
        </div>
      </div>
      {isCustom && introduction.outcome === 'accepted' && (
        <div className='bg-secondary-brand-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-circle' />
          This experteer wants to collaborate on a custom project with you. That means you'll
          discuss potential projects together over a Discovery Call.
        </div>
      )}
      <div className='bg-gray-25 py-3 px-4 border border-gray-200 rounded-lg font-gray-700 min-h-44 break-words'>
        {introduction.personalMessage}
      </div>
      {introduction.outcome === 'expired' && !userInitiatedIntro && (
        <div className='bg-error-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-triangle' />
          {`This introduction expired ${introExpirationDate.toLocaleDateString('en-CA', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}. You have 7 days to respond to new introductions`}
        </div>
      )}
      {introduction.outcome === 'declined' && !userInitiatedIntro && (
        <div className='bg-warning-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-circle' />
          {`You declined this introduction on ${new Date(
            introduction.statusChangedAt,
          ).toLocaleDateString('en-CA', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}`}
        </div>
      )}
      {introduction.outcome === 'accepted' && (
        <div className='bg-secondary-brand-25 border border-gray-200 p-4 font-gray-500 text-sm flex justify-left gap-4 items-center'>
          <Icon type='alert-circle' />
          Remember, you can connect with more than one Experteer, you aren’t committing to working
          together yet.
        </div>
      )}
    </div>
  );
};
